import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ShoppingCart, Loader2, Plus } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { apiRequest, formatPrice } from '../lib/utils';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

export default function RelatedProducts({ productId, title = "Souvent achetés ensemble" }) {
    const { user } = useAuth();
    const { refreshCart } = useCart();
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [addingToCart, setAddingToCart] = useState(null);

    useEffect(() => {
        loadRecommendations();
    }, [productId]);

    const loadRecommendations = async () => {
        try {
            const data = await apiRequest(`/recommendations/product/${productId}`);
            setProducts(data.recommendations || []);
        } catch (error) {
            console.error('Error loading recommendations:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleAddToCart = async (product) => {
        if (!user) {
            toast.error('Connectez-vous pour ajouter au panier');
            return;
        }

        setAddingToCart(product.product_id);
        try {
            await apiRequest('/cart/add', {
                method: 'POST',
                body: JSON.stringify({ product_id: product.product_id, quantity: 1 })
            });
            toast.success('Produit ajouté au panier');
            refreshCart();
        } catch (error) {
            toast.error(error.message);
        } finally {
            setAddingToCart(null);
        }
    };

    if (loading) {
        return (
            <div className="flex justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
        );
    }

    if (products.length === 0) {
        return null;
    }

    return (
        <Card data-testid="related-products">
            <CardHeader>
                <CardTitle className="flex items-center gap-2">
                    <Plus className="h-5 w-5 text-primary" />
                    {title}
                </CardTitle>
            </CardHeader>
            <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {products.map((product) => (
                        <div 
                            key={product.product_id}
                            className="group border rounded-lg overflow-hidden hover:shadow-md transition-shadow"
                        >
                            <Link to={`/shop/${product.product_id}`}>
                                <div className="aspect-square bg-muted relative overflow-hidden">
                                    <img
                                        src={product.image_url}
                                        alt={product.name}
                                        className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                                    />
                                    <Badge className="absolute top-2 left-2 text-xs bg-background/80 backdrop-blur">
                                        {product.brand}
                                    </Badge>
                                </div>
                            </Link>
                            <div className="p-3">
                                <Link to={`/shop/${product.product_id}`}>
                                    <h4 className="font-medium text-sm line-clamp-2 hover:text-primary transition-colors">
                                        {product.name}
                                    </h4>
                                </Link>
                                <div className="flex items-center justify-between mt-2">
                                    <span className="font-bold text-primary">
                                        {formatPrice(product.price)}
                                    </span>
                                    <Button
                                        size="icon"
                                        variant="ghost"
                                        className="h-8 w-8"
                                        onClick={() => handleAddToCart(product)}
                                        disabled={addingToCart === product.product_id}
                                    >
                                        {addingToCart === product.product_id ? (
                                            <Loader2 className="h-4 w-4 animate-spin" />
                                        ) : (
                                            <ShoppingCart className="h-4 w-4" />
                                        )}
                                    </Button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </CardContent>
        </Card>
    );
}

// Cart Recommendations Component
export function CartRecommendations() {
    const { user } = useAuth();
    const { refreshCart } = useCart();
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [addingToCart, setAddingToCart] = useState(null);

    useEffect(() => {
        loadRecommendations();
    }, []);

    const loadRecommendations = async () => {
        try {
            const data = await apiRequest('/recommendations/cart');
            setProducts(data.recommendations || []);
        } catch (error) {
            console.error('Error loading cart recommendations:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleAddToCart = async (product) => {
        if (!user) {
            toast.error('Connectez-vous pour ajouter au panier');
            return;
        }

        setAddingToCart(product.product_id);
        try {
            await apiRequest('/cart/add', {
                method: 'POST',
                body: JSON.stringify({ product_id: product.product_id, quantity: 1 })
            });
            toast.success('Produit ajouté au panier');
            refreshCart();
            loadRecommendations(); // Refresh recommendations
        } catch (error) {
            toast.error(error.message);
        } finally {
            setAddingToCart(null);
        }
    };

    if (loading || products.length === 0) {
        return null;
    }

    return (
        <div className="mt-8" data-testid="cart-recommendations">
            <h3 className="text-lg font-semibold mb-4">Complétez votre commande</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {products.map((product) => (
                    <div 
                        key={product.product_id}
                        className="group border rounded-lg overflow-hidden hover:shadow-md transition-shadow bg-card"
                    >
                        <Link to={`/shop/${product.product_id}`}>
                            <div className="aspect-square bg-muted relative overflow-hidden">
                                <img
                                    src={product.image_url}
                                    alt={product.name}
                                    className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                                />
                            </div>
                        </Link>
                        <div className="p-3">
                            <Link to={`/shop/${product.product_id}`}>
                                <h4 className="font-medium text-sm line-clamp-2 hover:text-primary transition-colors">
                                    {product.name}
                                </h4>
                            </Link>
                            <div className="flex items-center justify-between mt-2">
                                <span className="font-bold text-sm text-primary">
                                    {formatPrice(product.price)}
                                </span>
                                <Button
                                    size="sm"
                                    variant="secondary"
                                    onClick={() => handleAddToCart(product)}
                                    disabled={addingToCart === product.product_id}
                                >
                                    {addingToCart === product.product_id ? (
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                    ) : (
                                        <>
                                            <Plus className="h-3 w-3 mr-1" />
                                            Ajouter
                                        </>
                                    )}
                                </Button>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
